import React, { useMemo, useState } from 'react';
import { DownOutlined, ExclamationCircleFilled, RightOutlined } from '@ant-design/icons';

/**
 * One thing wrong with the declaration: where it is, and what is wrong there.
 *
 * The extraction used to build JSX as it walked, which meant the errors could
 * only ever be a flat list in the order the API happened to nest them — six
 * problems across five applications read as six unrelated lines each
 * re-stating the application it was in. As data they can be grouped.
 */
interface ValidationIssue {
    /** the application the problem is in, or Team / Declaration for the rest */
    scope: string;
    /** the way down to it inside that scope, e.g. ['VM', 'db1', 'memory'] */
    path: string[];
    message: string;
}

/**
 * The API nests errors as objects keyed by name, but any level of that nesting
 * can also be a plain string — "service not found" arrives where a map of
 * field errors was expected. `Object.entries` on a string enumerates its
 * characters, so one such message used to explode into one bogus row per
 * letter. Anything that is not a map of fields is the message it is.
 */
const isFieldMap = (value: any): value is Record<string, any> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const collectFields = (data: any, scope: string, path: string[]): ValidationIssue[] => {
    if (!isFieldMap(data)) {
        return [{ scope, path, message: String(data) }];
    }
    return Object.entries(data).map(([field, error]) => ({
        scope,
        path: [...path, field],
        message: String(error),
    }));
};

const collectServices = (services: any, appName: string): ValidationIssue[] => {
    if (!isFieldMap(services)) {
        return [{ scope: appName, path: [], message: String(services) }];
    }

    const issues: ValidationIssue[] = [];

    Object.entries(services).forEach(([serviceName, serviceData]: [string, any]) => {
        if (serviceName === 'metadata') {
            issues.push(...collectFields(serviceData, appName, ['Metadata']));
        } else if (serviceName === 'general') {
            issues.push({ scope: appName, path: [], message: String(serviceData) });
        } else if (isFieldMap(serviceData) && 'state' in serviceData) {
            issues.push({ scope: appName, path: [serviceName], message: String(serviceData.state) });
        } else if (!isFieldMap(serviceData)) {
            issues.push({ scope: appName, path: [serviceName], message: String(serviceData) });
        } else {
            Object.entries(serviceData).forEach(([serviceItemName, serviceItemData]) => {
                issues.push(...collectFields(serviceItemData, appName, [serviceName, serviceItemName]));
            });
        }
    });

    return issues;
};

const extractIssues = (jsonResponse: any): ValidationIssue[] => {
    const issues: ValidationIssue[] = [];

    Object.entries(jsonResponse ?? {}).forEach(([teamName, teamData]: [string, any]) => {
        if (['metadata', 'general'].includes(teamName)) {
            // metadata and general errors come back as strings, not team data
            issues.push({ scope: 'Declaration', path: [], message: String(teamData) });
            return;
        }

        if (!isFieldMap(teamData)) {
            issues.push({ scope: 'Declaration', path: [teamName], message: String(teamData) });
            return;
        }

        Object.entries(teamData).forEach(([appName, appData]: [string, any]) => {
            if (appName === 'metadata') {
                issues.push(...collectFields(appData, 'Team', ['Metadata']));
            } else if (appName === 'general') {
                issues.push({ scope: 'Team', path: [], message: String(appData) });
            } else {
                issues.push(...collectServices(appData, appName));
            }
        });
    });

    return issues;
};

/**
 * A failed validation, grouped by what it is wrong with.
 *
 * It leads with the shape of the failure — how many problems, across how many
 * applications — because that is what decides whether you fix it here or go
 * back to the declaration. Each application is then its own block, so a
 * repeated problem reads as one service's fault rather than as five.
 */
const ValidationErrors = ({ errors }: { errors: any }) => {
    const [open, setOpen] = useState(true);

    const groups = useMemo(() => {
        const issues = extractIssues(errors);
        const byScope = new Map<string, ValidationIssue[]>();
        issues.forEach((issue) => {
            const found = byScope.get(issue.scope);
            if (found) found.push(issue);
            else byScope.set(issue.scope, [issue]);
        });
        return [...byScope.entries()].map(([scope, items]) => ({ scope, items }));
    }, [errors]);

    const total = groups.reduce((sum, group) => sum + group.items.length, 0);

    return (
        <section className="sb-errors" role="alert" aria-live="polite">
            <div className="sb-errors__panel">
                <button
                    type="button"
                    className="sb-errors__bar"
                    aria-expanded={open}
                    aria-controls="sb-errors-body"
                    onClick={() => setOpen((wasOpen) => !wasOpen)}
                >
                    <span className="sb-errors__ico" aria-hidden="true">
                        <ExclamationCircleFilled />
                    </span>
                    <span className="sb-errors__heading">
                        <span className="sb-errors__title">Validation failed</span>
                        {/* the count first, because it is what decides what to do next */}
                        <span className="sb-errors__sub">
                            {total} {total === 1 ? 'problem' : 'problems'}
                            {groups.length > 1 && ` across ${groups.length} places`}
                        </span>
                    </span>
                    <span className="sb-errors__chev" aria-hidden="true">
                        {open ? <DownOutlined /> : <RightOutlined />}
                    </span>
                </button>

                <div id="sb-errors-body" className="sb-errors__body" hidden={!open}>
                    {groups.map((group) => (
                        <div className="sb-errors__group" key={group.scope}>
                            <h4 className="sb-errors__scope">
                                {group.scope}
                                <span className="sb-errors__scope-count">{group.items.length}</span>
                            </h4>
                            <ul className="sb-errors__list">
                                {group.items.map((issue, index) => (
                                    <li className="sb-errors__item" key={`${issue.path.join('/')}-${index}`}>
                                        {issue.path.length > 0 && (
                                            <span className="sb-errors__path">
                                                {issue.path.map((step, stepIndex) => (
                                                    <React.Fragment key={step + stepIndex}>
                                                        {stepIndex > 0 && (
                                                            <span className="sb-errors__sep" aria-hidden="true">/</span>
                                                        )}
                                                        <span className="sb-errors__step">{step}</span>
                                                    </React.Fragment>
                                                ))}
                                            </span>
                                        )}
                                        <span className="sb-errors__msg">{issue.message}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ValidationErrors;
